<script setup lang="ts">
import type { TableInstance } from 'element-plus'
import type Sortable from 'sortablejs'
import type { SchemaFormItem, SchemaFormItemScope, SchemaFormModel, SchemaFormOption, SchemaFormSubFormColumn, SchemaFormSubFormOptions } from './types'
import { FaButton, FaEmpty } from '@fantastic-admin/components'
import {
  ElCascader,
  ElCheckbox,
  ElCheckboxButton,
  ElCheckboxGroup,
  ElColorPicker,
  ElForm,
  ElFormItem,
  ElInput,
  ElInputNumber,
  ElOption,
  ElRadio,
  ElRadioButton,
  ElRadioGroup,
  ElRate,
  ElSelect,
  ElSelectV2,
  ElSlider,
  ElSwitch,
  ElTable,
  ElTableColumn,
  ElTimeSelect,
  ElTransfer,
  ElTreeSelect,
  ElUpload,
} from 'element-plus'
import { computed, defineComponent, h, nextTick, onBeforeUnmount, onMounted, ref, toRaw, watch } from 'vue'
import AppDatePicker from '@/components/AppForm/DatePicker.vue'
import AppTimePicker from '@/components/AppForm/TimePicker.vue'
import NestedSchemaForm from './index.vue'

defineOptions({
  name: 'FaSchemaFormSubFormArray',
})

const props = withDefaults(defineProps<{
  modelValue?: SchemaFormModel[]
  config: SchemaFormSubFormOptions
  disabled?: boolean
  size?: '' | 'large' | 'default' | 'small'
}>(), {
  modelValue: () => [],
  disabled: false,
  size: undefined,
})

const emit = defineEmits<{
  'update:modelValue': [value: SchemaFormModel[]]
}>()

const tableRef = ref<TableInstance>()
const rowKeys = ref<string[]>([])
let sortableInstance: Sortable | undefined

interface TableRowRecord {
  index: number
  key: string
  row: SchemaFormModel
}

const builtinComponentMap: Record<string, any> = {
  cascader: ElCascader,
  colorPicker: ElColorPicker,
  datePicker: AppDatePicker,
  input: ElInput,
  inputNumber: ElInputNumber,
  number: ElInputNumber,
  rate: ElRate,
  select: ElSelect,
  selectV2: ElSelectV2,
  slider: ElSlider,
  switch: ElSwitch,
  textarea: ElInput,
  timePicker: AppTimePicker,
  timeSelect: ElTimeSelect,
  transfer: ElTransfer,
  treeSelect: ElTreeSelect,
  upload: ElUpload,
}

const rows = computed(() => Array.isArray(props.modelValue) ? props.modelValue : [])
const resolvedLayout = computed(() => props.config.layout === 'card' ? 'group' : (props.config.layout ?? 'table'))
const addButtonText = computed(() => props.config.addButtonText ?? '新增')
const emptyText = computed(() => props.config.emptyText ?? '暂无数据')
const canSort = computed(() => resolvedLayout.value === 'table' && props.config.sortable && !props.disabled && rows.value.length > 1)

const rowFormOptions = computed(() => ({
  labelWidth: 96,
  cols: 2,
  size: props.size,
}))

const tableRows = computed<TableRowRecord[]>(() => {
  return rows.value.map((row, index) => ({
    index,
    key: getRowKeyByIndex(index),
    row,
  }))
})

const resolvedColumns = computed<SchemaFormSubFormColumn[]>(() => {
  if (props.config.columns?.length) {
    return props.config.columns
  }

  return props.config.items
    .filter(item => item.prop && item.label)
    .slice(0, 3)
    .map(item => ({
      label: String(typeof item.label === 'function' ? item.label() : item.label),
      prop: String(typeof item.prop === 'function' ? item.prop() : item.prop),
    }))
})

const columnItemMap = computed<Record<string, SchemaFormItem>>(() => {
  return Object.fromEntries(
    props.config.items
      .filter(item => item.prop)
      .map(item => [resolveItemProp(item), item]),
  )
})

const InlineFieldRenderer = defineComponent({
  name: 'FaSchemaFormSubFormInlineFieldRenderer',
  props: {
    item: {
      type: Object,
      required: true,
    },
    row: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: undefined,
    },
  },
  setup(renderProps) {
    return () => renderField(renderProps.item as SchemaFormItem, renderProps.row as SchemaFormModel)
  },
})

function clonePlain<T>(value: T): T {
  const rawValue = toRaw(value)
  if (Array.isArray(rawValue)) {
    return rawValue.map(item => clonePlain(item)) as T
  }
  if (rawValue && typeof rawValue === 'object') {
    const prototype = Object.getPrototypeOf(rawValue)
    if (prototype === Object.prototype || prototype === null) {
      return Object.fromEntries(
        Object.entries(rawValue as Record<string, unknown>).map(([key, current]) => [key, clonePlain(current)]),
      ) as T
    }
  }
  return rawValue
}

function emitRows(nextRows: SchemaFormModel[]) {
  emit('update:modelValue', clonePlain(nextRows))
}

function createRowKey() {
  return `subform-row-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

function syncRowKeys(targetLength: number) {
  const nextKeys = rowKeys.value.slice(0, targetLength)
  while (nextKeys.length < targetLength) {
    nextKeys.push(createRowKey())
  }
  rowKeys.value = nextKeys
}

function getRowKeyByIndex(index: number) {
  return rowKeys.value[index] ?? `subform-row-fallback-${index}`
}

function createDefaultRow() {
  const defaultValue = typeof props.config.defaultValue === 'function'
    ? props.config.defaultValue()
    : (props.config.defaultValue ?? {})
  return clonePlain(defaultValue)
}

function appendRow() {
  if (props.disabled) {
    return
  }
  syncRowKeys(rows.value.length + 1)
  emitRows([...rows.value, createDefaultRow()])
}

function removeRow(index: number) {
  if (props.disabled) {
    return
  }
  const nextRows = clonePlain(rows.value)
  nextRows.splice(index, 1)
  const nextKeys = rowKeys.value.slice()
  nextKeys.splice(index, 1)
  rowKeys.value = nextKeys
  emitRows(nextRows)
}

function resolveItemProp(item: SchemaFormItem) {
  if (typeof item.prop === 'function') {
    return String(item.prop())
  }
  return String(item.prop ?? '')
}

function createScope(item: SchemaFormItem, row: SchemaFormModel): SchemaFormItemScope {
  const prop = resolveItemProp(item)
  return {
    item,
    model: row,
    formData: row,
    formRef: undefined,
    disabled: Boolean(props.disabled),
    readonly: false,
    size: props.size,
    prop,
    value: row[prop],
    setValue: (value) => {
      row[prop] = value
    },
  }
}

function resolveItemOptions(item: SchemaFormItem, row: SchemaFormModel): SchemaFormOption[] {
  const scope = createScope(item, row)
  if (typeof item.options === 'function') {
    return item.options(scope)
  }
  return item.options ?? []
}

function resolveRenderProps(item: SchemaFormItem, row: SchemaFormModel) {
  const scope = createScope(item, row)
  if (typeof item.renderProps === 'function') {
    return item.renderProps(scope)
  }
  return item.renderProps ?? {}
}

function resolveBuiltinComponent(item: SchemaFormItem, row: SchemaFormModel) {
  const renderType = String(item.render ?? 'input')
  const options = resolveItemOptions(item, row)

  if (renderType === 'radio') {
    return options.length ? ElRadioGroup : ElRadio
  }
  if (renderType === 'checkbox') {
    return options.length ? ElCheckboxGroup : ElCheckbox
  }
  if (renderType === 'radioGroup' || renderType === 'radioButton') {
    return ElRadioGroup
  }
  if (renderType === 'checkboxGroup' || renderType === 'checkboxButton') {
    return ElCheckboxGroup
  }

  return builtinComponentMap[renderType] ?? ElInput
}

function resolveExtraProps(item: SchemaFormItem, row: SchemaFormModel) {
  const renderType = String(item.render ?? 'input')
  const options = resolveItemOptions(item, row)

  if (renderType === 'textarea') {
    return { type: 'textarea' }
  }
  if (renderType === 'cascader') {
    return { options }
  }
  if (renderType === 'selectV2') {
    return { options }
  }
  if (renderType === 'treeSelect') {
    return { data: options }
  }
  if (renderType === 'transfer') {
    return {
      data: options.map(option => ({
        key: option.value,
        ...option,
      })),
    }
  }

  return {}
}

function buildOptionChildren(item: SchemaFormItem, row: SchemaFormModel) {
  const renderType = String(item.render ?? 'input')
  const options = resolveItemOptions(item, row)

  if (!options.length) {
    return undefined
  }

  if (renderType === 'select') {
    return () => options.map(option =>
      h(ElOption, {
        key: `${resolveItemProp(item)}-${option.value}`,
        label: option.label,
        value: option.value,
        disabled: option.disabled,
      }))
  }

  if (renderType === 'radio' || renderType === 'radioGroup') {
    return () => options.map(option =>
      h(ElRadio, {
        key: `${resolveItemProp(item)}-${option.value}`,
        value: option.value,
        disabled: option.disabled,
      }, () => option.label))
  }

  if (renderType === 'radioButton') {
    return () => options.map(option =>
      h(ElRadioButton, {
        key: `${resolveItemProp(item)}-${option.value}`,
        value: option.value,
        disabled: option.disabled,
      }, () => option.label))
  }

  if (renderType === 'checkbox' || renderType === 'checkboxGroup') {
    return () => options.map(option =>
      h(ElCheckbox, {
        key: `${resolveItemProp(item)}-${option.value}`,
        value: option.value,
        disabled: option.disabled,
      }, () => option.label))
  }

  if (renderType === 'checkboxButton') {
    return () => options.map(option =>
      h(ElCheckboxButton, {
        key: `${resolveItemProp(item)}-${option.value}`,
        value: option.value,
        disabled: option.disabled,
      }, () => option.label))
  }

  return undefined
}

function getGroupTitle(row: SchemaFormModel, index: number) {
  const title = props.config.groupTitle
  if (typeof title === 'function') {
    return title(row, index)
  }
  if (typeof title === 'string' && title) {
    return title
  }
  return `子表单项 ${index + 1}`
}

function getGroupDescription(row: SchemaFormModel, index: number) {
  const description = props.config.groupDescription
  if (typeof description === 'function') {
    return description(row, index)
  }
  if (typeof description === 'string') {
    return description
  }
  return ''
}

function getRowOrderLabel(index: number) {
  return String(index + 1).padStart(2, '0')
}

function renderField(item: SchemaFormItem, row: SchemaFormModel) {
  const prop = resolveItemProp(item)
  const component = resolveBuiltinComponent(item, row)
  const renderProps = resolveRenderProps(item, row)
  const extraProps = resolveExtraProps(item, row)
  const optionChildren = buildOptionChildren(item, row)

  return h(component, {
    ...extraProps,
    ...renderProps,
    'modelValue': row[prop],
    'size': renderProps.size ?? props.size ?? 'small',
    'disabled': renderProps.disabled ?? props.disabled,
    'type': String(item.render ?? 'input') === 'textarea' ? 'textarea' : renderProps.type,
    'onUpdate:modelValue': (value: any) => {
      row[prop] = value
      renderProps['onUpdate:modelValue']?.(value)
    },
  }, optionChildren ? { default: optionChildren } : undefined)
}

async function initSortable() {
  if (resolvedLayout.value !== 'table') {
    sortableInstance?.destroy()
    sortableInstance = undefined
    return
  }

  const SortableModule = await import('sortablejs')
  const SortableCtor = SortableModule.default
  const tbody = tableRef.value?.$el?.querySelector('.el-table__body-wrapper tbody') as HTMLElement | null

  if (!tbody) {
    return
  }

  sortableInstance?.destroy()
  sortableInstance = new SortableCtor(tbody, {
    animation: 180,
    disabled: !canSort.value,
    draggable: 'tr',
    handle: '.drag-handle',
    ghostClass: 'subform-sort-ghost',
    chosenClass: 'subform-sort-chosen',
    onUpdate: (event) => {
      if (event.oldIndex == null || event.newIndex == null || event.oldIndex === event.newIndex) {
        return
      }
      const nextRows = clonePlain(rows.value)
      const [movedRow] = nextRows.splice(event.oldIndex, 1)
      nextRows.splice(event.newIndex, 0, movedRow)
      const nextKeys = rowKeys.value.slice()
      const [movedKey] = nextKeys.splice(event.oldIndex, 1)
      nextKeys.splice(event.newIndex, 0, movedKey)
      rowKeys.value = nextKeys
      emitRows(nextRows)
      nextTick(() => {
        tableRef.value?.doLayout()
      })
    },
  })
}

onMounted(() => {
  nextTick(() => {
    void initSortable()
  })
})

watch(
  () => [rows.value.length, canSort.value, resolvedLayout.value],
  () => {
    syncRowKeys(rows.value.length)
    nextTick(() => {
      void initSortable()
    })
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  sortableInstance?.destroy()
  sortableInstance = undefined
})
</script>

<template>
  <div class="fa-subform space-y-4">
    <div v-if="resolvedLayout === 'table'" class="subform-table-wrap border-default bg-default/80 border rounded-4">
      <ElTable ref="tableRef" :data="tableRows" row-key="key" :border="false" empty-text=" " class="subform-table">
        <ElTableColumn label="#" width="64" align="center">
          <template #default="{ $index }">
            <div class="row-order-badge">
              {{ getRowOrderLabel($index) }}
            </div>
          </template>
        </ElTableColumn>

        <ElTableColumn v-if="props.config.sortable" width="52" align="center">
          <template #default>
            <button type="button" class="drag-handle" :disabled="!canSort">
              <FaIcon name="i-material-symbols:drag-indicator" class="size-4" />
            </button>
          </template>
        </ElTableColumn>

        <ElTableColumn
          v-for="column in resolvedColumns"
          :key="String(column.prop)"
          :label="column.label"
          :prop="String(column.prop)"
          :width="column.width"
          :min-width="column.minWidth ?? 170"
          :align="column.align ?? 'left'"
        >
          <template #default="{ row: currentRow }">
            <ElForm label-position="top" class="w-full">
              <ElFormItem class="mb-0">
                <InlineFieldRenderer
                  :item="columnItemMap[String(column.prop)] ?? { prop: String(column.prop), render: 'input' }"
                  :row="currentRow.row"
                  :disabled="Boolean(disabled)"
                  :size="size"
                />
              </ElFormItem>
            </ElForm>
          </template>
        </ElTableColumn>

        <ElTableColumn label="" width="64" align="center" fixed="right">
          <template #default="{ row: currentRow }">
            <FaButton
              type="button"
              size="sm"
              variant="outline"
              class="delete-btn"
              :disabled="disabled"
              @click="removeRow(currentRow.index)"
            >
              <FaIcon name="i-ep:delete" class="size-4" />
            </FaButton>
          </template>
        </ElTableColumn>
      </ElTable>

      <div v-if="rows.length === 0" class="subform-empty-state px-4 py-10">
        <FaEmpty :title="emptyText" class="py-0 md:py-0" />
      </div>

      <div class="subform-table-footer">
        <div v-if="props.config.sortable" class="subform-table-tip">
          拖动左侧手柄可调整顺序
        </div>

        <FaButton type="button" size="sm" variant="outline" class="subform-add-btn gap-1.5" :disabled="disabled" @click="appendRow">
          <FaIcon name="i-ep:plus" class="size-4" />
          {{ addButtonText }}
        </FaButton>
      </div>
    </div>

    <div v-else class="space-y-4">
      <div class="flex justify-end">
        <FaButton type="button" size="sm" variant="outline" class="subform-add-btn gap-1.5" :disabled="disabled" @click="appendRow">
          <FaIcon name="i-ep:plus" class="size-4" />
          {{ addButtonText }}
        </FaButton>
      </div>

      <div v-if="rows.length" class="space-y-4">
        <div
          v-for="(groupRow, index) in rows"
          :key="getRowKeyByIndex(index)"
          class="group-block border-default bg-default px-5 py-4 border rounded-4"
        >
          <div class="group-block__header mb-4 flex gap-3 items-start justify-between">
            <div class="group-block__heading">
              <div class="group-block__index">
                {{ getRowOrderLabel(index) }}
              </div>

              <div class="min-w-0">
                <div class="text-main text-sm font-semibold truncate">
                  {{ getGroupTitle(groupRow, index) }}
                </div>
                <div v-if="getGroupDescription(groupRow, index)" class="group-block__desc mt-1">
                  {{ getGroupDescription(groupRow, index) }}
                </div>
              </div>
            </div>

            <FaButton
              type="button"
              size="sm"
              variant="outline"
              class="delete-btn"
              :title="props.config.deleteButtonText ?? '删除'"
              :disabled="disabled"
              @click="removeRow(index)"
            >
              <FaIcon name="i-ep:delete" class="size-4" />
            </FaButton>
          </div>

          <div class="group-block__body">
            <NestedSchemaForm
              v-model="rows[index]"
              :items="props.config.items as SchemaFormItem[]"
              :options="rowFormOptions"
            />
          </div>
        </div>
      </div>

      <div v-else class="subform-empty-state px-4 py-10">
        <FaEmpty :title="emptyText" class="py-0 md:py-0" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.subform-table-wrap {
  overflow: hidden;
  background: linear-gradient(180deg, rgb(255 255 255 / 96%), rgb(248 250 252 / 96%));
  border-color: rgb(148 163 184 / 18%);
  box-shadow: 0 16px 36px rgb(15 23 42 / 5%);
}

.subform-table-footer {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: flex-end;
  padding: 0.875rem 1rem 1rem;
  background: rgb(248 250 252 / 78%);
  border-top: 1px solid rgb(148 163 184 / 12%);
}

.subform-table-tip {
  margin-right: auto;
  font-size: 0.75rem;
  line-height: 1.25rem;
  color: rgb(71 85 105 / 82%);
}

.subform-add-btn {
  min-width: 6.5rem;
  padding-inline: 0.95rem;
  background: rgb(255 255 255 / 92%);
  border-radius: 9999px;
  box-shadow: 0 4px 12px rgb(15 23 42 / 5%);
}

.subform-empty-state {
  background: rgb(248 250 252 / 56%);
}

.row-order-badge,
.group-block__index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  height: 2rem;
  padding-inline: 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: rgb(71 85 105 / 92%);
  background: rgb(241 245 249 / 96%);
  border-radius: 9999px;
}

.drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  color: var(--el-text-color-secondary);
  cursor: grab;
  background: transparent;
  border: none;
  border-radius: 9999px;
  transition: all 0.2s ease;
}

.drag-handle:hover {
  color: var(--el-text-color-primary);
  background: rgb(148 163 184 / 12%);
}

.drag-handle:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.delete-btn {
  width: 2rem;
  min-width: 2rem;
  height: 2rem;
  padding: 0;
  color: var(--el-color-danger);
  background: rgb(255 255 255 / 92%);
  border-radius: 9999px;
  box-shadow: 0 4px 10px rgb(15 23 42 / 4%);
}

.group-block {
  background: linear-gradient(180deg, rgb(255 255 255 / 98%), rgb(248 250 252 / 92%));
  border-color: rgb(148 163 184 / 18%);
  box-shadow: 0 14px 32px rgb(15 23 42 / 5%);
}

.group-block__header {
  padding-bottom: 0.875rem;
  border-bottom: 1px solid rgb(148 163 184 / 12%);
}

.group-block__heading {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  min-width: 0;
}

.group-block__body {
  padding-top: 0.5rem;
}

.group-block__desc {
  font-size: 0.75rem;
  line-height: 1.25rem;
  color: rgb(71 85 105 / 92%);
}

:deep(.group-block__body .el-row) {
  row-gap: 1rem;
}

:deep(.group-block__body .el-form-item) {
  margin-bottom: 0;
}

:deep(.subform-table .el-table__inner-wrapper::before) {
  display: none;
}

:deep(.subform-table .el-table__header-wrapper) {
  background: rgb(248 250 252 / 92%);
}

:deep(.subform-table th.el-table__cell) {
  height: 3.1rem;
  font-weight: 600;
  color: rgb(71 85 105 / 96%);
  background: rgb(248 250 252 / 92%);
  border-bottom: 1px solid rgb(148 163 184 / 16%);
}

:deep(.subform-table td.el-table__cell),
:deep(.subform-table th.el-table__cell) {
  border-right: none !important;
}

:deep(.subform-table td.el-table__cell) {
  vertical-align: top;
  background: transparent;
  border-bottom: 1px solid rgb(148 163 184 / 10%);
}

:deep(.subform-table .cell) {
  padding-top: 0.45rem;
  padding-bottom: 0.45rem;
}

:deep(.subform-table .el-input__wrapper),
:deep(.subform-table .el-textarea__inner),
:deep(.subform-table .el-select__wrapper) {
  border-radius: 0.875rem;
}

:deep(.subform-table .el-form-item) {
  --el-form-item-margin-bottom: 0;
}

:deep(.subform-sort-ghost > td) {
  background: rgb(59 130 246 / 8%) !important;
}

:deep(.subform-sort-chosen > td) {
  background: rgb(59 130 246 / 5%) !important;
}
</style>
